/**
 * Add Provider Dialog
 *
 * Creates a new provider using the survey-based registration backend
 * (healthcare: `provider`, non-healthcare: `provider_nh` per backend).
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { SurveyFormRenderer } from '@/components/registration/SurveyFormRenderer'
import { useLocalization } from '@/contexts/LocalizationContext'
import type { RegistrationProfileType } from '@/types/survey'

export type AddProviderSurveyProfileType = Extract<
  RegistrationProfileType,
  'provider' | 'provider_nh'
>

interface AddProviderDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Survey / registration profile type from the backend */
  profileType?: AddProviderSurveyProfileType
  /**
   * `accounts.Entity.id` for the selected org (`registrationDefaultEntityId`).
   * Sent as `entity_id` on survey submit. Pass only from the Providers page; omit everywhere else.
   */
  providersPageOrganizationEntityId?: string | null
  /**
   * Fixes the provider affiliation code (e.g. `partner`, `nonhealthcare`) and hides the
   * in-form provider-type picker. Passed straight through to the survey renderer.
   */
  defaultProviderType?: string
  /** Called after successful registration; use to invalidate providers list etc. */
  onSuccess?: () => void | Promise<void>
}

export function AddProviderDialog({
  open,
  onOpenChange,
  profileType = 'provider',
  providersPageOrganizationEntityId = null,
  defaultProviderType,
  onSuccess,
}: AddProviderDialogProps) {
  const { t } = useLocalization()

  const handleSuccess = async () => {
    await onSuccess?.()
    onOpenChange(false)
  }

  // Non-healthcare is signalled either by the dedicated profile flow or the `nonhealthcare`
  // affiliation code (which is fetched under the `provider` profile with a query param).
  const isNonHealthcare = profileType === 'provider_nh' || defaultProviderType === 'nonhealthcare'
  const titleKey = isNonHealthcare
    ? 'providers.addNewNonHealthcareProvider'
    : 'providers.addNewProvider'
  const descriptionKey = isNonHealthcare
    ? 'providers.addNonHealthcareProviderDescription'
    : 'providers.addProviderDescription'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[720px] max-h-[90vh] overflow-y-auto p-0 gap-0">
        <DialogHeader className="p-6 pb-2">
          <DialogTitle>{t(titleKey)}</DialogTitle>
          <DialogDescription>{t(descriptionKey)}</DialogDescription>
        </DialogHeader>
        <div className="px-6 pb-6">
          <SurveyFormRenderer
            key={`${profileType}:${defaultProviderType ?? ''}`}
            profileType={profileType}
            providersPageOrganizationEntityId={providersPageOrganizationEntityId}
            defaultProviderType={defaultProviderType}
            onSuccess={handleSuccess}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
